'use client';

import React from 'react';
import type { ReactNode } from 'react';
import type { SelectedPersonalization } from './types';
import type { LeadGenerationInteractionProps } from 'components/analytics/types';
import { useSegmentTrack } from 'components/analytics/index';
import { useLeadGenerationInteraction } from 'components/analytics/hooks/useLeadGenerationInteraction';
import { ANALYTICS_EVENTS, LEAD_GENERATION_INTERACTION_TYPES } from 'components/analytics/types';
import { convertObjectToArray, getCookieValue } from 'components/analytics/utils';
import {
  AnalyticsModuleTrackingProvider,
  useAnalyticsModuleTracking,
} from 'components/analytics/AnalyticsModuleTracking';
import { COOKIE_KEY as DEVICE_ID_COOKIE } from '@1debit/device-intelligence';
import { trackExperimentViewed } from 'components/analytics/eventHandlerTracking';
import { getVariantDisplayName } from './getVariantDisplayName';
import { ANON_ID_COOKIE, NINETAILED_ANONYMOUS_ID_COOKIE } from 'utils/cookies';

/**
 * Fire-and-forget POST to the exposure-logging API route. Using a plain
 * fetch instead of a server action keeps the page out of the action
 * response re-render path, which was surfacing as 500s on the client.
 *
 * Sends the Chime device ID (not the Segment ID) as `anonymousId` so the
 * logged exposure matches the StatsigUser the adapter bucketed with.
 */
function logStatsigExposure(experimentId: string, anonymousId: string, segmentAnonymousId?: string): void {
  try {
    const url = typeof window !== 'undefined' ? window.location.href : undefined;
    fetch('/api/statsig-exposure', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ experimentId, anonymousId, segmentAnonymousId, url }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // best-effort
  }
}

interface ExposureTrackerProps {
  /** Experiment selection for this module, if it participates in an experiment */
  exposure?: SelectedPersonalization;
  /** Contentful sys.id of the module being tracked */
  moduleId: string;
  children: ReactNode;
}

// Backward compatibility — Ninetailed experiences use NINETAILED_EXPERIENCE_VIEWED
// To remove when Ninetailed is deprecated
const NinetailedExposureInner: React.FC<{ exposure: SelectedPersonalization }> = ({ exposure }) => {
  const hasTracked = React.useRef(false);
  const { attributes } = useAnalyticsModuleTracking();

  const exposureData = React.useMemo(() => {
    const isPersonalization = exposure.experienceType === 'nt_personalization';
    const isExperiment = exposure.experienceType === 'nt_experiment';

    let variantDisplayName: string;
    if (isPersonalization) {
      variantDisplayName = exposure.variantName ?? `Variant ${exposure.variantIndex}`;
    } else if (isExperiment) {
      variantDisplayName = exposure.variantIndex === 0 ? 'Control' : `Variant ${exposure.variantIndex}`;
    } else {
      variantDisplayName = exposure.variantIndex === 0 ? 'Baseline' : `Variant ${exposure.variantIndex}`;
    }

    return {
      ninetailed_id: getCookieValue(NINETAILED_ANONYMOUS_ID_COOKIE) ?? '',
      experience_name: exposure.experienceName ?? '',
      experience_id: exposure.experienceId,
      variant_name: variantDisplayName,
      variant_id: exposure.variantId ?? '',
      experience_type: exposure.experienceType ?? '',
    };
  }, [exposure]);

  const analyticsProps: LeadGenerationInteractionProps = React.useMemo(
    () => ({
      interaction_type: LEAD_GENERATION_INTERACTION_TYPES.NINETAILED_EXPERIENCE_VIEWED,
      interaction_details: {
        location: attributes.moduleLocation,
        action_detail: 'experience_viewed',
      },
      additional_data: convertObjectToArray(exposureData),
    }),
    [exposureData, attributes.moduleLocation],
  );

  const trackEvent = useLeadGenerationInteraction(analyticsProps);

  React.useEffect(() => {
    if (!hasTracked.current) {
      trackEvent();
      hasTracked.current = true;
    }
  }, [trackEvent]);

  return null;
};

const DefaultExposureInner: React.FC<{ exposure: SelectedPersonalization }> = ({ exposure }) => {
  const hasTracked = React.useRef(false);
  const { attributes } = useAnalyticsModuleTracking();

  const exposureData = React.useMemo(
    () => ({
      experiment_id: exposure.experienceId,
      variant_index: exposure.variantIndex,
      variant_id: exposure.variantId ?? '',
      variant_name: getVariantDisplayName(exposure.variantIndex),
    }),
    [exposure],
  );

  const analyticsProps: LeadGenerationInteractionProps = React.useMemo(
    () => ({
      interaction_type: LEAD_GENERATION_INTERACTION_TYPES.EXPERIMENT_VIEWED,
      interaction_details: {
        location: attributes.moduleLocation,
        action_detail: 'experience_viewed',
      },
      additional_data: convertObjectToArray(exposureData),
    }),
    [exposureData, attributes.moduleLocation],
  );

  const trackEvent = useSegmentTrack(
    {
      eventName: ANALYTICS_EVENTS.LEAD_GENERATION_INTERACTION,
      properties: analyticsProps,
      opts: { shouldSendToFacebook: false },
    },
    false,
  );

  React.useEffect(() => {
    if (!hasTracked.current) {
      trackEvent();

      trackExperimentViewed({
        experience_id: exposure.experienceId,
        variant_id: exposure.variantId ?? '',
        variant_name: getVariantDisplayName(exposure.variantIndex),
        variant_index: exposure.variantIndex,
      });

      if (exposure.vendor === 'statsig') {
        // anonymousId must be the Chime device ID (the same cookie the adapter
        // used for bucketing), not the Segment anonymous ID.
        const anonymousId = getCookieValue(DEVICE_ID_COOKIE);
        const segmentAnonymousId = getCookieValue(ANON_ID_COOKIE);
        if (anonymousId) {
          logStatsigExposure(exposure.experienceId, anonymousId, segmentAnonymousId);
        }
      }

      hasTracked.current = true;
    }
  }, [trackEvent, exposure]);

  return null;
};

// Fires exposure events once on mount, not on viewport entry.
export const ExposureTracker: React.FC<ExposureTrackerProps> = ({ exposure, moduleId: _moduleId, children }) => {
  if (!exposure) return <>{children}</>;

  return (
    <AnalyticsModuleTrackingProvider>
      {exposure.vendor === 'ninetailed' ? (
        <NinetailedExposureInner exposure={exposure} />
      ) : (
        <DefaultExposureInner exposure={exposure} />
      )}
      {children}
    </AnalyticsModuleTrackingProvider>
  );
};
